import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React from 'react';

import {
  FORM_ROW_LIMIT,
  TableOrFormRenderOptions,
  TableOrFormRenderOptionsType,
} from 'notebook/structured-tables/StructuredTableOrFormOutsideNotebook';
import {ProcessTaskSchemaWithFormsQuery} from 'processes/modals/__generated/ProcessTaskSchemaWithFormsQuery';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ToggleGroup, {ToggleGroupOption} from 'ui-library/ToggleGroup';

type FormDefinition = ProcessTaskSchemaWithFormsQuery['formDefinitions'][number];

const SHOW_AS_FORM_MAP = {
  [TableOrFormRenderOptions.FORM]: 'Form',
  [TableOrFormRenderOptions.TABLE]: 'Table',
};

export const TABLE_FORM_TOGGLE_BUTTON_OPTIONS: Array<ToggleGroupOption<TableOrFormRenderOptionsType>> =
  Object.entries(SHOW_AS_FORM_MAP).map(([renderOption, renderOptionText]) => ({
    buttonProps: {
      text: renderOptionText,
    },
    dataTestAttrs: {
      component: 'CreateProcessModal',
      element: 'ToggleGroup',
      key: renderOption as unknown as keyof typeof TableOrFormRenderOptions,
    },
    id: renderOption as unknown as keyof typeof TableOrFormRenderOptions,
  }));

/** Disabled Form/Table Toggle for use when form view is disabled */
export const TABLE_LOCKED_TABLE_FORM_TOGGLE_BUTTON_OPTIONS: Array<
  ToggleGroupOption<TableOrFormRenderOptionsType>
> = produce(TABLE_FORM_TOGGLE_BUTTON_OPTIONS, (draft) => {
  const formIndex = TABLE_FORM_TOGGLE_BUTTON_OPTIONS.findIndex(
    (op) => op.id === TableOrFormRenderOptions.FORM
  );
  draft[formIndex].popOverProps = {
    text: `The form view is only available for fewer than ${FORM_ROW_LIMIT} tasks`,
    disabled: false,
  };
});

export interface TableFormSelectorProps {
  selectedOption: TableOrFormRenderOptionsType;
  selectedForm?: FormDefinition;
  forms: Array<FormDefinition>;
  onTableFormSelect: (selectedOption: TableOrFormRenderOptionsType, selectedForm?: FormDefinition) => void;
  tableLockEnabled: boolean;
}

export default function TableFormSelector({
  selectedOption,
  selectedForm,
  forms,
  onTableFormSelect,
  tableLockEnabled,
}: TableFormSelectorProps): JSX.Element {
  const hasMultipleForms = forms.length > 1;
  const toggleOptions: Array<ToggleGroupOption<TableOrFormRenderOptionsType>> = [
    ...(tableLockEnabled ? TABLE_LOCKED_TABLE_FORM_TOGGLE_BUTTON_OPTIONS : TABLE_FORM_TOGGLE_BUTTON_OPTIONS),
  ].reverse(); // we display the options in the opposite order for users working with Custom Forms!

  return (
    <div className={classNames('tableFormSelector', css(styles.tableFormSelector))}>
      <ToggleGroup<TableOrFormRenderOptionsType>
        options={toggleOptions}
        selection={selectedOption}
        onChangeSelection={(selection) => {
          onTableFormSelect(selection, hasMultipleForms ? selectedForm : forms?.[0]);
        }}
        size={ButtonSize.MEDIUM}
        disabled={tableLockEnabled}
      />
      {hasMultipleForms && (
        <div className={classNames({[css(styles.formDropdownContainer)]: hasMultipleForms})}>
          <SelectDropdown
            displayName={selectedForm?.name}
            className={classNames('formSelectDropdown', css(styles.formDropdown))}
            disabled={selectedOption === TableOrFormRenderOptions.TABLE}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                {forms.map((form) => (
                  <Option
                    key={form.id}
                    onSelect={() => {
                      onTableFormSelect(TableOrFormRenderOptions.FORM, form);
                    }}
                  >
                    {form.name}
                  </Option>
                ))}
              </Menu>
            }
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  tableFormSelector: {
    display: 'flex',
  },
  formDropdownContainer: {
    marginLeft: '10px',
  },
  formDropdown: {
    width: '200px',
  },
});
